import type { EventDataType } from '@ixigo/analytics';
import { globalTracker } from '@/utils/track';
import { trackCbccGaEvent } from './ga';

// CleverTap + GA4 events for the CBCC (travel-credit-card) flow. Ported from
// fintech-ui's `cbccEventTracker`, which sends every event to both; see the CBCC
// CleverTap Event Reference. Names are the EXACT CleverTap event strings — do
// not rename, the analytics contract keys on them (GA gets them snake_cased).
export const CBCC_EVENT = {
	/** CVP landing page mounted (fires once per load). */
	CVP_PAGE_LOAD: 'CBCC CVP Page Load',
	/** CVP mount when a `source` query param is present (returned from elsewhere). */
	BACK_TO_IXIGO: 'CBCC Back to ixigo',
	/** User left the CVP page via a back press (in-page control or device back). */
	CVP_PAGE_BACKPRESS: 'CBCC CVP Page Backpress',
	/** Apply CTA clicked — fired before the login check (logged in or out). */
	APPLY_CTA: 'CBCC Apply CTA',
	/** Pincode eligibility API responded. */
	PINCODE_ELIGIBILITY: 'CBCC Pincode Eligibility',
	/** Personal-details (apply) form opened. */
	DETAILS_PAGE_LOAD: 'CBCC Personal Details Page Load',
	/** Apply form submitted (validation passed). */
	DETAILS_FORM_SUBMITTED: 'CBCC Personal Details Form Submitted',
	/** ONBOARDING flow handed off to the bank (redirect URL returned). */
	REDIRECTED_TO_BANK: 'CBCC User Redirected to Bank',
	/** APPROVED flow opened manage-card (redirect URL returned). */
	MANAGE_CARD: 'CBCC Manage Card',
} as const;

type CbccEventName = (typeof CBCC_EVENT)[keyof typeof CBCC_EVENT];

// CVP-derived properties sent on EVERY cbcc event (mirrors fintech-ui's
// getCommonData). They live in the route loader, not in the services/hooks that
// fire most events, so the CVP page pushes them here once (setCbccCommonProps)
// and trackCbccEvent merges them into every event. Module-level (not React
// state) so non-component call sites — the bank-redirect hooks, the pincode
// service — pick them up too. globalTracker.track already injects the app-wide
// commons (App ID / User ID / Device ID / Source), so these layer on top.
let cbccCommonProps: Partial<EventDataType> = {};

/**
 * Set the CVP-derived common props carried by every subsequent cbcc event.
 * Called from the CVP page once CVP resolves (and again after an in-page login
 * re-resolves it). Keys are the exact CleverTap contract keys.
 */
export function setCbccCommonProps(props: {
	status?: string;
	userEligibleToApply?: boolean;
	isUserLoggedIn: boolean;
}): void {
	cbccCommonProps = {
		'User Status': props.status ?? '',
		'Onboarding Eligibility':
			props.userEligibleToApply === undefined ? '' : props.userEligibleToApply,
		isUserLoggedIn: props.isUserLoggedIn,
	};
}

// `referrer` is the visit's `utm_source` — the same key fintech-ui's
// getCommonData sends. fintech-ui reads it off the URL on every event, which
// works there because its whole flow is one page. Here the apply form is its own
// route and the SPA navigate() to it drops the query string, so the value is
// remembered for the rest of the JS context (a utm_source on the current URL
// still wins). A hard reload forgets it, as it would in fintech-ui.
let cbccReferrer = '';

function getCbccReferrer(): string {
	if (typeof window === 'undefined') return cbccReferrer;
	const utmSource = new URLSearchParams(window.location.search).get(
		'utm_source',
	);
	if (utmSource) cbccReferrer = utmSource;
	return cbccReferrer;
}

/**
 * Track a CBCC event with the app-wide commons (via globalTracker) + the
 * CVP-derived commons (cbccCommonProps) + `referrer` + any per-event data, and
 * mirror it to GA4 (ga.ts) with the same properties.
 *
 * Every event carries `UI Version: 'new'` so analytics can distinguish this
 * (fin-apps / travel-credit-card) implementation from the legacy fintech-ui
 * CBCC flow that fires the same event names. Set first so per-event data can't
 * accidentally override it.
 */
export function trackCbccEvent(
	eventName: CbccEventName,
	eventData?: Partial<EventDataType>,
): void {
	const data: Partial<EventDataType> = {
		'UI Version': 'new',
		referrer: getCbccReferrer(),
		...cbccCommonProps,
		...eventData,
	};
	globalTracker.track(eventName, data);
	// globalTracker.track merges the app-wide commons in internally; merge them
	// the same way here so GA gets exactly what CleverTap gets.
	trackCbccGaEvent(eventName, {
		...globalTracker.getContext().getCommonAttributes?.(),
		...data,
	});
}
